import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { CityStateSelect } from '../components/ui/CityStateSelect';
import { SpecialitySelect } from '../components/ui/SpecialitySelect';
import { LogActivityModal, ActivityTimeline, EntityDeals } from './Accounts';

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

function SearchSelect({ label, value, onChange, options=[], placeholder, allowNew=false, style={} }) {
  const [open,setOpen]=useState(false); const [q,setQ]=useState(''); const ref=useRef(null);
  useEffect(()=>{ const h=(e)=>{ if(ref.current&&!ref.current.contains(e.target))setOpen(false); }; document.addEventListener('mousedown',h); return()=>document.removeEventListener('mousedown',h); },[]);
  const filtered=options.filter(o=>(o.label||o.value||o).toLowerCase().includes(q.toLowerCase()));
  const showAdd=allowNew&&q.trim()&&!options.find(o=>(o.value||o).toLowerCase()===q.trim().toLowerCase());
  const select=(val)=>{onChange(val);setQ('');setOpen(false);};
  const disp=options.find(o=>(o.value||o)===value);
  const dispLabel=disp?(disp.label||disp.value||disp):(value||'');
  return (
    <div ref={ref} style={{position:'relative',...style}}>
      {label&&<label className="form-label">{label}</label>}
      <div className="form-control" style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',userSelect:'none'}} onClick={()=>setOpen(o=>!o)}>
        <span style={{flex:1,color:dispLabel?'var(--text-primary)':'var(--text-muted)',fontSize:'0.875rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{dispLabel||placeholder||'Select…'}</span>
        {value&&<span onClick={e=>{e.stopPropagation();onChange('');setQ('');}} style={{color:'var(--text-muted)',fontSize:11,cursor:'pointer',flexShrink:0}}>✕</span>}
        <span style={{color:'var(--text-muted)',fontSize:9,flexShrink:0}}>{open?'▲':'▼'}</span>
      </div>
      {open&&(
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,zIndex:300,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)',boxShadow:'var(--shadow-lg)',maxHeight:240,display:'flex',flexDirection:'column'}}>
          <div style={{padding:'5px 7px',borderBottom:'1px solid var(--border)'}}><input autoFocus className="form-control" style={{padding:'5px 9px',fontSize:'0.82rem'}} placeholder="Type to search…" value={q} onChange={e=>setQ(e.target.value)} onClick={e=>e.stopPropagation()}/></div>
          <div style={{overflowY:'auto'}}>
            {showAdd&&<div onClick={()=>select(q.trim())} style={{padding:'9px 14px',cursor:'pointer',fontSize:'0.875rem',borderBottom:'1px solid var(--border)',color:'var(--brand-purple)',fontWeight:600}}>+ Add "{q.trim()}"</div>}
            {filtered.length===0&&!showAdd?<div style={{padding:'12px 14px',color:'var(--text-muted)',fontSize:'0.82rem',textAlign:'center'}}>{allowNew?'Type to add new':'No results'}</div>
              :filtered.map(opt=>{const v=opt.value||opt,l=opt.label||opt.value||opt; return <div key={v} onClick={()=>select(v)} style={{padding:'9px 14px',cursor:'pointer',fontSize:'0.875rem',borderBottom:'1px solid var(--border)',background:v===value?'var(--brand-purple-faint)':'transparent',color:v===value?'var(--brand-purple)':'var(--text-primary)',fontWeight:v===value?600:400}}>{l}</div>;})}
          </div>
        </div>
      )}
    </div>
  );
}

function useMasterData(category) {
  const [data,setData]=useState([]);
  useEffect(()=>{ api.get(`/master-data/${category}`).then(r=>setData(r.data.data)).catch(()=>{}); },[category]);
  return data;
}

// Institutions a doctor may practice at (e.g. Glenmark) — separate from
// distributor_account_id, and from Account Manager's per-device "used by"
// link; this is just the standing directory fact. Best-effort: silently
// empty if the logged-in role can't read accounts.
function useAccountOptions() {
  const [data,setData]=useState([]);
  useEffect(()=>{
    api.get('/accounts', { params:{ limit:1000, sort:'name', order:'asc' } })
      .then(r=>setData((r.data?.data||[]).map(a=>({ value:a.id, label:`${a.name}${a.account_type?` (${a.account_type})`:''}` }))))
      .catch(()=>{});
  },[]);
  return data;
}

const COUNTRIES = [
  {value:'India',flag:'🇮🇳'},{value:'Nepal',flag:'🇳🇵'},{value:'Bangladesh',flag:'🇧🇩'},
  {value:'Sri Lanka',flag:'🇱🇰'},{value:'Pakistan',flag:'🇵🇰'},{value:'Afghanistan',flag:'🇦🇫'},
  {value:'Myanmar',flag:'🇲🇲'},{value:'Bhutan',flag:'🇧🇹'},{value:'Maldives',flag:'🇲🇻'},
  {value:'United Arab Emirates',flag:'🇦🇪'},{value:'Saudi Arabia',flag:'🇸🇦'},{value:'Qatar',flag:'🇶🇦'},
  {value:'Kuwait',flag:'🇰🇼'},{value:'Bahrain',flag:'🇧🇭'},{value:'Oman',flag:'🇴🇲'},
  {value:'United States',flag:'🇺🇸'},{value:'United Kingdom',flag:'🇬🇧'},{value:'Canada',flag:'🇨🇦'},
  {value:'Australia',flag:'🇦🇺'},{value:'Singapore',flag:'🇸🇬'},{value:'Malaysia',flag:'🇲🇾'},
  {value:'Other',flag:'🌍'},
];

function CountrySelector({ value, onChange }) {
  const [q, setQ] = useState(value || 'India');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  useEffect(() => { setQ(value || 'India'); }, [value]);
  const filtered = COUNTRIES.filter(c => c.value.toLowerCase().includes(q.toLowerCase()));
  const selected = COUNTRIES.find(c => c.value === value);
  return (
    <div className="form-group">
      <label className="form-label">Country</label>
      <div style={{position:'relative'}} ref={ref}>
        <input className="form-control" value={open ? q : (selected ? `${selected.flag} ${selected.value}` : q)}
          onFocus={() => { setOpen(true); setQ(''); }}
          onChange={e => setQ(e.target.value)}
          placeholder="Search country…" />
        {open && (
          <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)',boxShadow:'0 8px 24px rgba(0,0,0,0.12)',zIndex:200,maxHeight:220,overflowY:'auto'}}>
            {filtered.map(c => (
              <div key={c.value} onClick={() => { onChange(c.value); setQ(c.value); setOpen(false); }}
                style={{padding:'9px 14px',cursor:'pointer',fontSize:'0.875rem',borderBottom:'1px solid var(--border)',background:c.value===value?'var(--brand-purple-faint)':'transparent',color:c.value===value?'var(--brand-purple)':'var(--text-primary)',fontWeight:c.value===value?600:400}}>
                {c.flag} {c.value}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

async function deleteDoctor(id, name, reload) {
  if (!window.confirm(`Delete Dr. ${name}?\nThis can be restored by an admin.`)) return;
  try { await api.delete(`/doctors/${id}`); toast.success('Doctor deleted'); reload(); }
  catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
}

export default function DoctorsPage() {
  const { can } = useAuth();
  const [doctors,setDoctors]=useState([]); const [meta,setMeta]=useState({total:0,limit:20}); const [loading,setLoading]=useState(true);
  const _q = new URLSearchParams(window.location.search);
  const [showAdd,setShowAdd]=useState(_q.get('new')==='1'); const [showFilters,setShowFilters]=useState(false); const [page,setPage]=useState(1);
  const prefillFromUrl = _q.get('new')==='1' ? { name:_q.get('name')||'', mobile:_q.get('phone')||'', city:_q.get('city')||'', territory_id:_q.get('territory_id')||'' } : null;
  const [viewDoctor,setViewDoctor]=useState(null); const [editDoctor,setEditDoctor]=useState(null);
  const [logActivityFor,setLogActivityFor]=useState(null);
  const [showBulkImport,setShowBulkImport]=useState(false);
  const [filters,setFilters]=useState({search:'',priority:'',fot_available:'',state:'',territory_id:'',journey_stage:'',speciality:'',district:''});
  const states=useMasterData('state'); const specialities=useMasterData('speciality'); const journeyStages=useMasterData('journey_stage');
  const [territories,setTerritories]=useState([]);
  const [districts,setDistricts]=useState([]);
  useEffect(()=>{ api.get('/territories').then(r=>setTerritories(r.data.data)).catch(()=>{}); },[]);
  useEffect(()=>{
    const params=filters.state?{state:filters.state}:{};
    api.get('/doctors/districts',{params}).then(r=>setDistricts(r.data.data||[])).catch(()=>{});
  },[filters.state]);

  const activeFilterCount=Object.entries(filters).filter(([k,v])=>k!=='search'&&v).length;

  const load=useCallback(async()=>{
    setLoading(true);
    try {
      const params={page,limit:20};
      Object.entries(filters).forEach(([k,v])=>{if(v)params[k]=v;});
      const {data}=await api.get('/doctors',{params});
      setDoctors(data.data); setMeta(data.meta);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  },[page,filters]);

  useEffect(()=>{load();},[load]);
  const setFilter=(k,v)=>{setFilters(f=>({...f,[k]:v}));setPage(1);};
  const clearFilters=()=>{setFilters({search:'',priority:'',fot_available:'',state:'',territory_id:'',journey_stage:'',speciality:'',district:''});setPage(1);};

  return (
    <AppLayout title="Doctors" subtitle={`${meta.total.toLocaleString()} total`}>
      <DoctorStats />

      <div className="filters-bar" style={{marginBottom:10}}>
        <div className="search-wrap" style={{flex:1,maxWidth:320}}>
          <span className="search-icon" style={{fontSize:13}}>🔍</span>
          <input className="form-control" placeholder="Search name, contact, district…" value={filters.search} onChange={e=>setFilter('search',e.target.value)}/>
        </div>
        <select className="form-control" style={{width:120}} value={filters.priority} onChange={e=>setFilter('priority',e.target.value)}>
          <option value="">All Priority</option>
          {['A','B','C'].map(p=><option key={p} value={p}>Priority {p}</option>)}
        </select>
        <select className="form-control" style={{width:115}} value={filters.fot_available} onChange={e=>setFilter('fot_available',e.target.value)}>
          <option value="">FOT — All</option>
          <option value="true">FOT: Yes</option>
          <option value="false">FOT: No</option>
        </select>
        <SearchSelect value={filters.state} onChange={v=>{setFilter('state',v);setFilter('district','');}} options={states} placeholder="All States" style={{width:150}}/>
        <SearchSelect value={filters.district} onChange={v=>setFilter('district',v)} options={districts.map(d=>({value:d,label:d}))} placeholder={filters.state?'All Cities':'All Cities'} style={{width:160}}/>
        <button className="btn btn-secondary btn-sm" style={{position:'relative'}} onClick={()=>setShowFilters(f=>!f)}>
          ⚙️ More Filters
          {activeFilterCount>0&&<span style={{position:'absolute',top:-6,right:-6,background:'var(--brand-purple)',color:'#fff',fontSize:'0.6rem',width:16,height:16,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700}}>{activeFilterCount}</span>}
        </button>
        {activeFilterCount>0&&<button className="btn btn-ghost btn-sm" onClick={clearFilters} style={{color:'var(--danger)'}}>✕ Clear</button>}
        <button className="btn btn-ghost btn-sm" onClick={load}>↻</button>
        {can('doctors','can_create')&&<button className="btn btn-primary btn-sm" onClick={()=>setShowAdd(true)}>+ Add Doctor</button>}
        {can('doctors','can_create')&&<button className="btn btn-secondary btn-sm" onClick={()=>setShowBulkImport(true)}>⬆ Bulk Import</button>}
        {can('doctors','can_export')&&<button className="btn btn-secondary btn-sm" onClick={()=>exportDoctors(filters)}>⬇ Export</button>}
      </div>

      {showFilters&&(
        <div className="card" style={{marginBottom:12,padding:'14px 18px'}}>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))',gap:'8px 14px',alignItems:'end'}}>
            <SearchSelect label="State" value={filters.state} onChange={v=>setFilter('state',v)} options={states} placeholder="Any state"/>
            <div>
              <label className="form-label">Territory</label>
              <select className="form-control" value={filters.territory_id} onChange={e=>setFilter('territory_id',e.target.value)}>
                <option value="">Any territory</option>
                {territories.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <SearchSelect label="Speciality" value={filters.speciality} onChange={v=>setFilter('speciality',v)} options={specialities} placeholder="Any speciality"/>
            <SearchSelect label="Journey Stage" value={filters.journey_stage} onChange={v=>setFilter('journey_stage',v)} options={journeyStages} placeholder="Any stage"/>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body no-pad">
          {loading?<div style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}><div className="spinner" style={{margin:'0 auto 12px'}}/>Loading…</div>:(
            <div className="table-wrap">
              <table>
                <thead><tr><th>#</th><th>Name</th><th>District / State</th><th>Speciality</th><th>Contact</th><th>Territory</th><th>Priority</th><th>FOT</th><th>Stage</th><th>LTV</th><th>Rep</th><th>Added By</th><th></th></tr></thead>
                <tbody>
                  {doctors.map(d=>(
                    <tr key={d.id}>
                      <td style={{color:'var(--text-muted)',fontSize:'0.78rem'}}>{d.serial_no}</td>
                      <td style={{fontWeight:600,whiteSpace:'nowrap'}}>
                        {d.name}
                        {d.status==='pending_verification'&&<span className="badge" style={{marginLeft:6,fontSize:'0.65rem',background:'#FFF3CD',color:'#856404',border:'1px solid #FFEAA7'}}>⏳ Unverified</span>}
                        {d.source==='conference'&&d.status!=='pending_verification'&&<span className="badge" style={{marginLeft:6,fontSize:'0.65rem',background:'#E8E7FF',color:'#5E5CE6'}}>Conference</span>}
                      </td>
                      <td style={{fontSize:'0.82rem',color:'var(--text-secondary)'}}>{d.district}<br/><span style={{color:'var(--text-muted)',fontSize:'0.72rem'}}>{d.normalized_state}</span></td>
                      <td style={{fontSize:'0.82rem'}}>{d.speciality||'—'}</td>
                      <td style={{fontSize:'0.82rem',fontFamily:'monospace'}}>{d.contact_number||'—'}</td>
                      <td>{d.territory_name&&<span className="badge badge-purple" style={{fontSize:'0.68rem'}}>{d.territory_name}</span>}</td>
                      <td><span className={`badge badge-${(d.priority||'b').toLowerCase()}`}>{d.priority||'—'}</span></td>
                      <td><span className={`badge ${d.fot_available?'badge-success':'badge-c'}`}>{d.fot_available?'Yes':'No'}</span></td>
                      <td style={{fontSize:'0.75rem',maxWidth:130,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'var(--text-secondary)'}}>{d.journey_stage||'—'}</td>
                      <td style={{fontSize:'0.82rem',fontWeight:600}}>{d.ltv_12m?`₹${(d.ltv_12m/1000).toFixed(0)}K`:'—'}</td>
                      <td style={{fontSize:'0.78rem',color:'var(--text-secondary)'}}>{d.assigned_name||'—'}</td>
                      <td style={{fontSize:'0.75rem',color:'var(--text-muted)',whiteSpace:'nowrap'}}>{d.created_by_name||'—'}</td>
                      <td style={{display:'flex',gap:4,padding:'6px 8px'}}>
                        <button className="btn btn-ghost btn-sm" style={{padding:'3px 8px',fontSize:'0.75rem'}}
                          onClick={()=>setViewDoctor(d)}>View</button>
                        <button className="btn btn-ghost btn-sm" style={{padding:'3px 8px',fontSize:'0.75rem',color:'var(--brand-purple)'}}
                          onClick={()=>setEditDoctor(d)}>Edit</button>
                        <button className="btn btn-ghost btn-sm" style={{padding:'3px 8px',fontSize:'0.75rem',color:'#059669'}}
                          onClick={e=>{e.stopPropagation();setLogActivityFor({id:d.id,name:d.name});}} title="Log Activity">📞</button>
                        {can('doctors','can_delete')&&<button className="btn btn-ghost btn-sm" style={{padding:'3px 8px',fontSize:'0.75rem',color:'var(--danger)'}}
                          onClick={()=>deleteDoctor(d.id,d.name,load)}><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg></button>}
                      </td>
                    </tr>
                  ))}
                  {!doctors.length&&!loading&&<tr><td colSpan="12" style={{textAlign:'center',padding:'48px',color:'var(--text-muted)'}}>No doctors found.{activeFilterCount>0&&<button className="btn btn-ghost btn-sm" onClick={clearFilters} style={{marginLeft:8}}>Clear filters</button>}</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {meta.total>meta.limit&&(
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14,fontSize:'0.82rem',color:'var(--text-secondary)'}}>
          <span>Showing {((page-1)*meta.limit)+1}–{Math.min(page*meta.limit,meta.total)} of {meta.total.toLocaleString()}</span>
          <div style={{display:'flex',gap:6}}>
            <button className="btn btn-secondary btn-sm" onClick={()=>setPage(p=>p-1)} disabled={page===1}>← Prev</button>
            <span style={{padding:'4px 8px'}}>Page {page}</span>
            <button className="btn btn-secondary btn-sm" onClick={()=>setPage(p=>p+1)} disabled={page*meta.limit>=meta.total}>Next →</button>
          </div>
        </div>
      )}

      {showAdd&&<AddDoctorModal onClose={()=>setShowAdd(false)} onSaved={load} prefill={prefillFromUrl}/>}
      {viewDoctor&&<DoctorDetailModal doctor={viewDoctor} onClose={()=>setViewDoctor(null)} onEdit={()=>{setEditDoctor(viewDoctor);setViewDoctor(null);}}/>}
      {editDoctor&&<EditDoctorModal doctor={editDoctor} onClose={()=>setEditDoctor(null)} onSaved={()=>{setEditDoctor(null);load();}}/>}
      {showBulkImport&&<BulkImportModal onClose={()=>setShowBulkImport(false)} onSaved={load}/>}
      {logActivityFor&&<LogActivityModal entityType="doctor" entityId={logActivityFor.id} entityName={logActivityFor.name} onClose={()=>setLogActivityFor(null)} onSaved={()=>setLogActivityFor(null)}/>}
    </AppLayout>
  );
}

function DoctorStats() {
  const [stats,setStats]=useState(null);
  useEffect(()=>{api.get('/doctors/stats').then(r=>setStats(r.data.data)).catch(()=>{});},[]);
  if(!stats)return null;
  return (
    <div style={{display:'flex',gap:10,marginBottom:16,flexWrap:'wrap'}}>
      {[{l:'Total',v:Number(stats.total||0).toLocaleString(),c:'#403EC9'},{l:'Priority A',v:Number(stats.priority_a||0),c:'#16A34A'},{l:'Priority B',v:Number(stats.priority_b||0),c:'#2563EB'},{l:'FOT Yes',v:Number(stats.fot_yes||0),c:'#D97706'},{l:'Recurring',v:Number(stats.installed_base||0),c:'#7C3AED'}].map(s=>(
        <div key={s.l} style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)',padding:'8px 14px',display:'flex',gap:7,alignItems:'center'}}>
          <div style={{width:7,height:7,borderRadius:'50%',background:s.c}}/>
          <span style={{fontSize:'0.75rem',color:'var(--text-secondary)'}}>{s.l}</span>
          <span style={{fontWeight:700,fontSize:'0.9rem'}}>{s.v}</span>
        </div>
      ))}
    </div>
  );
}

function AddDoctorModal({ onClose, onSaved, prefill }) {
  const [form,setForm]=useState({
    name: prefill?.name ? (prefill.name.match(/^Dr\./i) ? prefill.name : 'Dr. '+prefill.name) : '',
    country: 'India',
    state: prefill?.state||'', town: prefill?.city||'', district:'',
    speciality:'',qualification:'',
    contact_number: prefill?.phone||prefill?.mobile||prefill?.contact_number||'', contact_email:'',
    address:'', zip_code:'', gstin:'',
    priority:'B',fot_available:false,
    territory_id: prefill?.territory_id||'',
    journey_stage:'prospect', notes:'', enterprise_account_id:''
  });
  const [saving,setSaving]=useState(false);
  const [territories,setTerritories]=useState([]);
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const states=useMasterData('state'); const journeyStagesAdd=useMasterData('journey_stage');
  const accountOptions=useAccountOptions();
  useEffect(()=>{ api.get('/territories').then(r=>setTerritories(r.data.data)).catch(()=>{}); },[]);
  // Once territories load, resolve territory_id from pre-filled state if not already set by prefill
  useEffect(()=>{
    if(!territories.length||!form.state||form.territory_id) return;
    const terrName=STATE_TERRITORY_MAP[form.state];
    const terr=territories.find(t=>t.name===terrName);
    if(terr) set('territory_id',terr.id);
  },[territories]); // form.state/territory_id intentionally omitted — only re-run when territories load
  const isIndia = form.country === 'India';
  const save=async(e)=>{e.preventDefault();if(isIndia&&!form.state){toast.error('Please select a State — it determines the doctor\'s territory');return;}if(form.gstin&&!GSTIN_RE.test(form.gstin)){toast.error('GSTIN is not a valid 15-character GSTIN');return;}setSaving(true);try{await api.post('/doctors',form);toast.success('Doctor added!');onSaved();onClose();}catch(err){toast.error(err.response?.data?.message||'Failed');}finally{setSaving(false);}};
  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:640}}>
        <div className="modal-header"><h3>Add Doctor</h3><button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button></div>
        <div className="modal-body">
          <form onSubmit={save}>
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <div style={{display:'flex',alignItems:'center',border:'1px solid var(--border)',borderRadius:'var(--radius-md)',overflow:'hidden',background:'var(--bg-card)'}}>
                <span style={{padding:'0 10px',background:'var(--bg)',borderRight:'1px solid var(--border)',color:'var(--text-secondary)',fontSize:'0.875rem',fontWeight:600,whiteSpace:'nowrap',height:'100%',display:'flex',alignItems:'center'}}>Dr.</span>
                <input className="form-control" style={{border:'none',borderRadius:0,flex:1}} value={form.name.replace(/^Dr\.\s*/i,'')} onChange={e=>set('name','Dr. '+e.target.value)} required placeholder="Firstname Lastname"/>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 16px'}}>
              <div className="form-group" style={{gridColumn:'1/-1'}}>
                <CountrySelector value={form.country} onChange={v=>{ set('country',v); if(v!=='India'){set('state','');set('territory_id','');} }} />
              </div>
              <div className="form-group" style={{gridColumn:'1/-1'}}>
                {isIndia ? (
                  <CityStateSelect
                    city={form.town} state={form.state}
                    onCityChange={v=>set('town',v)}
                    onStateChange={v=>{ set('state',v); const terrName=STATE_TERRITORY_MAP[v]; const terr=territories.find(t=>t.name===terrName); set('territory_id',terr?.id||''); }}
                    stateOptions={states.map(s=>s.value)}
                    stateRequired
                  />
                ) : (
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0 16px'}}>
                    <div className="form-group"><label className="form-label">City</label><input className="form-control" value={form.town} onChange={e=>set('town',e.target.value)} placeholder="City"/></div>
                    <div className="form-group"><label className="form-label">State / Province</label><input className="form-control" value={form.state} onChange={e=>set('state',e.target.value)} placeholder="State or Province"/></div>
                  </div>
                )}
              </div>
              <SpecialitySelect value={form.speciality} onChange={v=>set('speciality',v)} />
              <div className="form-group"><SearchSelect label="Journey Stage" value={form.journey_stage} onChange={v=>set('journey_stage',v)} options={journeyStagesAdd} placeholder="Select stage…"/></div>
              <div className="form-group"><label className="form-label">Contact Number</label><input className="form-control" value={form.contact_number} onChange={e=>set('contact_number',e.target.value)} placeholder="9876543210"/></div>
              <div className="form-group"><label className="form-label">Email</label><input type="email" className="form-control" value={form.contact_email} onChange={e=>set('contact_email',e.target.value)} placeholder="dr@example.com"/></div>
              <div className="form-group" style={{gridColumn:'1/-1'}}><label className="form-label">Address</label><input className="form-control" value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Building / Street / Locality"/></div>
              <div className="form-group" style={{gridColumn:'1/-1'}}>
                <SearchSelect label="Affiliated Enterprise Account (optional)" value={form.enterprise_account_id} onChange={v=>set('enterprise_account_id',v)} options={accountOptions} placeholder="e.g. Glenmark — if this doctor practices at an institution"/>
              </div>
              <div className="form-group"><label className="form-label">Zip / Pin Code</label><input className="form-control" value={form.zip_code} onChange={e=>set('zip_code',e.target.value)} placeholder="e.g. 411001"/></div>
              <div className="form-group"><label className="form-label">GSTIN</label><input className="form-control" value={form.gstin} onChange={e=>set('gstin',e.target.value.toUpperCase())} placeholder="27AAJCR9213N1ZS" maxLength={15}/></div>
              <div className="form-group"><label className="form-label">Priority</label>
                <select className="form-control" value={form.priority} onChange={e=>set('priority',e.target.value)}>
                  <option value="A">A — High (every 30 days)</option>
                  <option value="B">B — Medium (every 45 days)</option>
                  <option value="C">C — Low (every 60 days)</option>
                </select>
              </div>
              <div className="form-group" style={{display:'flex',alignItems:'center',paddingTop:22}}>
                <label style={{display:'flex',alignItems:'center',gap:10,cursor:'pointer'}}>
                  <input type="checkbox" checked={form.fot_available} onChange={e=>set('fot_available',e.target.checked)} style={{width:16,height:16,accentColor:'var(--brand-purple)'}}/>
                  <div><div style={{fontWeight:600,fontSize:'0.875rem'}}>FOT Available</div><div style={{fontSize:'0.72rem',color:'var(--text-muted)'}}>Device already installed</div></div>
                </label>
              </div>
            </div>
            {isIndia&&STATE_TERRITORY_MAP[form.state]&&<div style={{background:'var(--brand-purple-faint)',border:'1px solid var(--brand-purple)',borderRadius:'var(--radius-md)',padding:'10px 14px',marginBottom:12,fontSize:'0.82rem'}}>🗺️ Will be assigned to <strong>{STATE_TERRITORY_MAP[form.state]}</strong> territory</div>}
            {!isIndia&&<div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:'var(--radius-md)',padding:'10px 14px',marginBottom:12,fontSize:'0.82rem'}}>🌍 International — no territory assignment</div>}
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={saving}>{saving?'Adding…':'Add Doctor'}</button></div>
          </form>
        </div>
      </div>
    </div>
  );
}

async function exportDoctors(filters) {
  try {
    const params=Object.fromEntries(Object.entries(filters).filter(([,v])=>v));
    const {data}=await api.get('/doctors/export',{params});
    const csv=[['Serial','Name','State','District','Speciality','Contact','Email','Territory','Priority','FOT','Journey Stage','LTV'],...data.data.map(d=>[d.serial_no,d.name,d.state,d.district,d.speciality,d.contact_number,d.contact_email,d.territory,d.priority,d.fot_available,d.journey_stage,d.ltv_annual])].map(r=>r.map(c=>`"${c??''}"`).join(',')).join('\n');
    const a=document.createElement('a');a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));a.download='doctors_export.csv';a.click();
  } catch { toast.error('Export failed'); }
}

// ─── Doctor Detail Modal ──────────────────────────────────────────
function DoctorDetailModal({ doctor: d, onClose, onEdit }) {
  const [profile, setProfile] = useState({});
  const [showLogActivity, setShowLogActivity] = useState(false);
  const [actRefreshKey, setActRefreshKey] = useState(0);
  useEffect(() => {
    api.get(`/doctors/${d.id}/profile`)
      .then(r => setProfile(r.data.data || {}))
      .catch(() => setProfile({}));
  }, [d.id]);

  const initials = (d.name || '?').replace(/^Dr\.?\s*/i, '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const PRIORITY_BG = { A:'rgba(34,197,94,0.85)', B:'rgba(217,119,6,0.85)', C:'rgba(107,114,128,0.85)' };
  const JOURNEY_BG = { converted:'#16A34A', recurring:'#2563EB', demo_done:'#7C3AED', prospect:'#6B7280', demo_scheduled:'#D97706' };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',zIndex:1000,backdropFilter:'blur(2px)'}}/>
      {/* Drawer */}
      <div style={{position:'fixed',top:0,right:0,bottom:0,width:500,maxWidth:'95vw',background:'#fff',zIndex:1001,overflowY:'auto',boxShadow:'-8px 0 40px rgba(0,0,0,0.15)',display:'flex',flexDirection:'column'}}>

        {/* Gradient header */}
        <div style={{background:'linear-gradient(135deg, #0f4c81 0%, #1a73c8 100%)',padding:'24px 24px 20px',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:16}}>
            <div style={{display:'flex',alignItems:'center',gap:14}}>
              <div style={{width:52,height:52,borderRadius:'50%',background:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem',fontWeight:800,color:'#fff',flexShrink:0,border:'2px solid rgba(255,255,255,0.4)'}}>
                {initials || '🩺'}
              </div>
              <div>
                <div style={{color:'rgba(255,255,255,0.7)',fontSize:'0.68rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:3}}>
                  🩺 {d.speciality || 'Doctor'}
                </div>
                <div style={{color:'#fff',fontWeight:800,fontSize:'1.1rem',lineHeight:1.2}}>{d.name}</div>
                {(d.district || d.normalized_state || d.state) && (
                  <div style={{color:'rgba(255,255,255,0.75)',fontSize:'0.82rem',marginTop:2}}>
                    📍 {[d.district, d.normalized_state || d.state].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
            </div>
            <button onClick={onClose} style={{background:'rgba(255,255,255,0.15)',border:'none',borderRadius:8,width:32,height:32,cursor:'pointer',color:'#fff',fontSize:'1rem',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
          </div>
          {/* Status pills */}
          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
            <span style={{background: PRIORITY_BG[d.priority] || 'rgba(107,114,128,0.85)',color:'#fff',borderRadius:20,padding:'3px 12px',fontSize:'0.72rem',fontWeight:700}}>Priority {d.priority}</span>
            <span style={{background: JOURNEY_BG[d.journey_stage] || '#6B7280',color:'#fff',borderRadius:20,padding:'3px 12px',fontSize:'0.72rem',fontWeight:700,textTransform:'capitalize'}}>{d.journey_stage?.replace(/_/g,' ') || 'prospect'}</span>
            {d.fot_available && <span style={{background:'rgba(34,197,94,0.85)',color:'#fff',borderRadius:20,padding:'3px 12px',fontSize:'0.72rem',fontWeight:700}}>FOT ✓</span>}
            {d.ltv_12m > 0 && <span style={{marginLeft:'auto',background:'rgba(255,255,255,0.2)',color:'#fff',borderRadius:20,padding:'3px 12px',fontSize:'0.72rem',fontWeight:700}}>LTV ₹{(d.ltv_12m/1000).toFixed(0)}K</span>}
          </div>
        </div>

        {/* Body */}
        <div style={{flex:1,padding:'20px 24px',display:'flex',flexDirection:'column',gap:14}}>

          {/* Quick actions */}
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>setShowLogActivity(true)} style={{flex:1,padding:'8px 0',borderRadius:8,border:'1.5px solid #1a73c8',color:'#1a73c8',background:'#fff',cursor:'pointer',fontWeight:700,fontSize:'0.82rem'}}>📞 Log Activity</button>
            <button onClick={onEdit} style={{flex:1,padding:'8px 0',borderRadius:8,border:'none',background:'linear-gradient(135deg,#0f4c81,#1a73c8)',color:'#fff',cursor:'pointer',fontWeight:700,fontSize:'0.82rem'}}>✏️ Edit Doctor</button>
          </div>

          {/* Contact card */}
          <div style={{background:'#F0F7FF',borderRadius:10,padding:'14px 16px',border:'1px solid #BFDBFE'}}>
            <div style={{fontSize:'0.68rem',fontWeight:700,color:'#1a73c8',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>📇 Contact Details</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 16px'}}>
              {d.contact_number && <div><div style={{fontSize:'0.68rem',color:'#6B7280',marginBottom:2}}>Phone</div><a href={`tel:${d.contact_number}`} style={{color:'#1a73c8',textDecoration:'none',fontWeight:600,fontSize:'0.88rem'}}>{d.contact_number}</a></div>}
              {d.contact_email && <div><div style={{fontSize:'0.68rem',color:'#6B7280',marginBottom:2}}>Email</div><a href={`mailto:${d.contact_email}`} style={{color:'#1a73c8',textDecoration:'none',fontWeight:600,fontSize:'0.88rem',wordBreak:'break-all'}}>{d.contact_email}</a></div>}
              {d.territory_name && <div><div style={{fontSize:'0.68rem',color:'#6B7280',marginBottom:2}}>Territory</div><div style={{fontWeight:600,fontSize:'0.88rem'}}>{d.territory_name}</div></div>}
              {d.qualification && <div><div style={{fontSize:'0.68rem',color:'#6B7280',marginBottom:2}}>Qualification</div><div style={{fontWeight:600,fontSize:'0.88rem'}}>{d.qualification}</div></div>}
              {d.enterprise_account_name && <div><div style={{fontSize:'0.68rem',color:'#6B7280',marginBottom:2}}>Affiliated Account</div><div style={{fontWeight:600,fontSize:'0.88rem'}}>{d.enterprise_account_name}</div></div>}
            </div>
          </div>

          {/* Sales Playbook */}
          {(profile.focus_product_1 || profile.focus_product_2 || profile.growth_play || profile.outcome_goal_90d) && (
            <div style={{background:'#F5F3FF',borderRadius:10,padding:'14px 16px',border:'1px solid #DDD6FE'}}>
              <div style={{fontSize:'0.68rem',fontWeight:700,color:'#7C3AED',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>📋 Sales Playbook</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 16px'}}>
                {profile.focus_product_1 && <div><div style={{fontSize:'0.68rem',color:'#7C3AED',marginBottom:2}}>Focus Product 1</div><div style={{fontWeight:600,fontSize:'0.88rem',color:'#26215C'}}>{profile.focus_product_1}</div></div>}
                {profile.focus_product_2 && <div><div style={{fontSize:'0.68rem',color:'#7C3AED',marginBottom:2}}>Focus Product 2</div><div style={{fontWeight:600,fontSize:'0.88rem',color:'#26215C'}}>{profile.focus_product_2}</div></div>}
                {profile.growth_play && <div style={{gridColumn:'1/-1'}}><div style={{fontSize:'0.68rem',color:'#7C3AED',marginBottom:2}}>Growth Play</div><div style={{fontWeight:500,fontSize:'0.88rem',color:'#26215C'}}>{profile.growth_play}</div></div>}
                {profile.outcome_goal_90d && <div style={{gridColumn:'1/-1'}}><div style={{fontSize:'0.68rem',color:'#7C3AED',marginBottom:2}}>90D Outcome Goal</div><div style={{fontWeight:500,fontSize:'0.88rem',color:'#26215C'}}>{profile.outcome_goal_90d}</div></div>}
                {profile.coverage_model && <div><div style={{fontSize:'0.68rem',color:'#7C3AED',marginBottom:2}}>Coverage Model</div><div style={{fontWeight:500,fontSize:'0.88rem',color:'#26215C'}}>{profile.coverage_model}</div></div>}
                {profile.ltv_12m > 0 && <div><div style={{fontSize:'0.68rem',color:'#7C3AED',marginBottom:2}}>LTV (12m)</div><div style={{fontWeight:600,fontSize:'0.88rem',color:'#26215C'}}>₹{(profile.ltv_12m/1000).toFixed(0)}K</div></div>}
              </div>
            </div>
          )}

          {/* Deals */}
          <div style={{background:'#F9FAFB',borderRadius:10,padding:'14px 16px',border:'1px solid #E5E7EB'}}>
            <div style={{fontSize:'0.68rem',fontWeight:700,color:'#7C3AED',textTransform:'uppercase',letterSpacing:'0.07em',marginBottom:10}}>🏷 Pipeline Deals</div>
            <EntityDeals entityType="doctor" entityId={d.id} />
          </div>

          {/* Activity Timeline */}
          <div style={{background:'#F9FAFB',borderRadius:10,padding:'14px 16px',border:'1px solid #E5E7EB'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
              <div style={{fontSize:'0.68rem',fontWeight:700,color:'#6B7280',textTransform:'uppercase',letterSpacing:'0.07em'}}>🕐 Activity Timeline</div>
              <button onClick={()=>setShowLogActivity(true)} style={{padding:'4px 12px',borderRadius:6,border:'1.5px solid #1a73c8',color:'#1a73c8',background:'#fff',cursor:'pointer',fontWeight:700,fontSize:'0.75rem'}}>+ Log</button>
            </div>
            <ActivityTimeline entityType="doctor" entityId={d.id} refreshKey={actRefreshKey} />
          </div>

          <div style={{fontSize:'0.72rem',color:'#9CA3AF',textAlign:'center',paddingBottom:8}}>
            #{d.serial_no} · Added {d.created_at ? new Date(d.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}) : '—'}
          </div>
        </div>
      </div>
      {showLogActivity && (
        <LogActivityModal entityType="doctor" entityId={d.id} entityName={d.name}
          onClose={()=>setShowLogActivity(false)}
          onSaved={()=>{setShowLogActivity(false);setActRefreshKey(k=>k+1);}} />
      )}
    </>
  );
}

// ─── Edit Doctor Modal ────────────────────────────────────────────
const STATE_TERRITORY_MAP = {'Gujarat':'West','Rajasthan':'West','Delhi':'North','Uttar Pradesh':'North','Punjab':'North','Madhya Pradesh':'Central','Chhattisgarh':'Central','Maharashtra':'Maharashtra & Institutional','Telangana':'South-East','Andhra Pradesh':'South-East','Tamil Nadu':'South-West','Kerala':'South-West'};

function EditDoctorModal({ doctor, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:           doctor.name || '',
    country:        doctor.country || 'India',
    town:           doctor.town || '',
    state:          doctor.normalized_state || doctor.state || '',
    territory_id:   doctor.territory_id || '',
    speciality:     doctor.speciality || '',
    priority:       doctor.priority || 'B',
    journey_stage:  doctor.journey_stage || 'prospect',
    fot_available:  doctor.fot_available || false,
    contact_number: doctor.contact_number || '',
    contact_email:  doctor.contact_email || '',
    address:        doctor.address || '',
    zip_code:       doctor.zip_code || '',
    gstin:          doctor.gstin || '',
    notes:          doctor.notes || '',
    enterprise_account_id: doctor.enterprise_account_id || '',
  });
  const isIndia = form.country === 'India';
  const [saving, setSaving] = useState(false);
  const [territories, setTerritories] = useState([]);
  const states = useMasterData('state');
  const accountOptions = useAccountOptions();
  useEffect(() => { api.get('/territories').then(r => setTerritories(r.data.data || [])).catch(() => {}); }, []);

  const set = (k, v) => setForm(prev => ({...prev, [k]: v}));

  const save = async () => {
    if (form.gstin && !GSTIN_RE.test(form.gstin)) { toast.error('GSTIN is not a valid 15-character GSTIN'); return; }
    setSaving(true);
    try {
      await api.put(`/doctors/${doctor.id}`, form);
      toast.success('Doctor updated');
      onSaved();
    } catch(err) {
      toast.error(err?.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const resolvedTerrName = territories.find(t => t.id === form.territory_id)?.name;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={onClose}>
      <div style={{background:'var(--bg-card)',borderRadius:12,width:560,maxHeight:'90vh',overflow:'auto',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}} onClick={e=>e.stopPropagation()}>
        <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center',position:'sticky',top:0,background:'var(--bg-card)',zIndex:1}}>
          <h2 style={{fontSize:'1rem',fontWeight:700,margin:0}}>Edit — {doctor.name}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{padding:20,display:'flex',flexDirection:'column',gap:14}}>
          <div className="form-group">
            <label className="form-label">Doctor Name</label>
            <div style={{display:'flex',alignItems:'center',border:'1px solid var(--border)',borderRadius:'var(--radius-md)',overflow:'hidden',background:'var(--bg-card)'}}>
              <span style={{padding:'0 10px',background:'var(--bg)',borderRight:'1px solid var(--border)',color:'var(--text-secondary)',fontSize:'0.875rem',fontWeight:600,whiteSpace:'nowrap',height:'100%',display:'flex',alignItems:'center'}}>Dr.</span>
              <input className="form-control" style={{border:'none',borderRadius:0,flex:1}} value={form.name.replace(/^Dr\.\s*/i,'')} onChange={e=>set('name','Dr. '+e.target.value)} placeholder="Firstname Lastname"/>
            </div>
          </div>

          {/* Country / City / State / Territory */}
          <CountrySelector value={form.country} onChange={v => { set('country', v); if (v !== 'India') { set('state', ''); set('territory_id', ''); } }} />
          {isIndia ? (
            <>
              <CityStateSelect
                city={form.town} state={form.state}
                onCityChange={v => set('town', v)}
                onStateChange={v => {
                  set('state', v);
                  const terrName = STATE_TERRITORY_MAP[v];
                  const terr = territories.find(t => t.name === terrName);
                  if (terr) set('territory_id', terr.id);
                }}
                stateOptions={states.map(s => s.value)}
              />
              <div className="form-group">
                <label className="form-label">Territory</label>
                <select className="form-control" value={form.territory_id} onChange={e => set('territory_id', e.target.value)}>
                  <option value="">— Not assigned</option>
                  {territories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {resolvedTerrName && <div style={{fontSize:'0.75rem',color:'var(--brand-purple)',marginTop:4}}>Territory: {resolvedTerrName}</div>}
              </div>
            </>
          ) : (
            <>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div className="form-group"><label className="form-label">City</label><input className="form-control" value={form.town} onChange={e=>set('town',e.target.value)} placeholder="City"/></div>
                <div className="form-group"><label className="form-label">State / Province</label><input className="form-control" value={form.state} onChange={e=>set('state',e.target.value)} placeholder="State or Province"/></div>
              </div>
              <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:'var(--radius-md)',padding:'10px 14px',fontSize:'0.82rem'}}>🌍 International — no territory assignment</div>
            </>
          )}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <SpecialitySelect value={form.speciality} onChange={v=>set('speciality',v)} />
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-control" value={form.priority} onChange={e=>set('priority',e.target.value)}>
                <option value="A">A — High potential</option>
                <option value="B">B — Medium potential</option>
                <option value="C">C — Low potential</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Journey Stage</label>
            <select className="form-control" value={form.journey_stage} onChange={e=>set('journey_stage',e.target.value)}>
              <option value="prospect">Prospect</option>
              <option value="contacted">Contacted</option>
              <option value="demo_scheduled">Demo Scheduled</option>
              <option value="demo_done">Demo Done</option>
              <option value="evaluating">Evaluating</option>
              <option value="converted">Converted</option>
              <option value="recurring">Recurring</option>
              <option value="kol_advocate">KOL / Advocate</option>
              <option value="dormant">Dormant</option>
            </select>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div className="form-group">
              <label className="form-label">Contact Number</label>
              <input className="form-control" value={form.contact_number} onChange={e=>set('contact_number',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" type="email" value={form.contact_email} onChange={e=>set('contact_email',e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-control" value={form.address||''} onChange={e=>set('address',e.target.value)} placeholder="Building / Street / Locality" />
          </div>
          <div className="form-group">
            <label className="form-label">Zip / Pin Code</label>
            <input className="form-control" value={form.zip_code||''} onChange={e=>set('zip_code',e.target.value)} placeholder="e.g. 411001" style={{maxWidth:160}} />
          </div>
          <div className="form-group">
            <label className="form-label">GSTIN</label>
            <input className="form-control" value={form.gstin||''} onChange={e=>set('gstin',e.target.value.toUpperCase())} placeholder="27AAJCR9213N1ZS" maxLength={15} style={{maxWidth:220}} />
          </div>
          <div className="form-group">
            <label className="form-label" style={{display:'flex',alignItems:'center',gap:8}}>
              <input type="checkbox" checked={form.fot_available} onChange={e=>set('fot_available',e.target.checked)} />
              alveoFlow FOT device installed at this doctor's clinic
            </label>
          </div>
          <div className="form-group">
            <SearchSelect label="Affiliated Enterprise Account (optional)" value={form.enterprise_account_id} onChange={v=>set('enterprise_account_id',v)} options={accountOptions} placeholder="e.g. Glenmark — if this doctor practices at an institution"/>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)} />
          </div>
          <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:4}}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const CSV_TEMPLATE_HEADERS = 'serial_no,name,state,district,town,speciality,qualification,contact,email,priority,journey_stage,fot_available,dob,doa';
const CSV_TEMPLATE_EXAMPLE = '1001,Dr. Ravi Sharma,Maharashtra,Pune,Kothrud,Pulmonologist,MD,9876543210,ravi@clinic.com,A,prospect,Yes,,';

// Map display/alias column names to what the backend expects
const COL_ALIASES = {
  'doctor name':'name','full name':'name','dr name':'name','doctor':'name',
  'mobile':'contact','phone':'contact','mobile no':'contact','phone no':'contact','contact number':'contact','contact_number':'contact',
  'email id':'email','email address':'email',
  'specialization':'speciality','specialization':'speciality','spec':'speciality',
  'qualification':'qualification','qual':'qualification',
  'dist':'district',
  'city':'town','area':'town','locality':'town',
  'journey stage':'journey_stage','stage':'journey_stage',
  'fot':'fot_available','fot available':'fot_available',
  'sr no':'serial_no','sr':'serial_no','s.no':'serial_no','sno':'serial_no','serial':'serial_no',
  'prior':'priority',
};

function normalizeHeader(h) {
  const lower = h.trim().toLowerCase().replace(/[^a-z0-9 _]/g,'').replace(/\s+/g,' ');
  return COL_ALIASES[lower] || lower.replace(/ /g,'_');
}

function parseCsvToJson(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) throw new Error('CSV must have header row + at least one data row');
  const headers = lines[0].split(',').map(normalizeHeader);
  return lines.slice(1).filter(l => l.trim()).map((line) => {
    // Handle quoted fields containing commas
    const vals = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      if (line[i] === '"') { inQ = !inQ; }
      else if (line[i] === ',' && !inQ) { vals.push(cur.trim()); cur = ''; }
      else { cur += line[i]; }
    }
    vals.push(cur.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (vals[i] || '').replace(/^"|"$/g,'').trim(); });
    return obj;
  });
}

function BulkImportModal({ onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f); setError(''); setResult(null);
    const isExcel = f.name.match(/\.(xlsx|xls)$/i);
    try {
      if (isExcel) {
        const XLSX = await import('xlsx');
        const buf = await f.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(ws, { defval: '' });
        // Normalize headers
        const rows = raw.map(row => {
          const obj = {};
          Object.keys(row).forEach(k => { obj[normalizeHeader(k)] = String(row[k] ?? '').trim(); });
          return obj;
        });
        if (!rows.length) throw new Error('Excel file is empty');
        setPreview(rows);
      } else {
        const reader = new FileReader();
        reader.onload = (ev) => {
          try { setPreview(parseCsvToJson(ev.target.result)); }
          catch(err) { setError(err.message); setPreview(null); }
        };
        reader.readAsText(f);
      }
    } catch(err) {
      setError(err.message || 'Failed to read file'); setPreview(null);
    }
  };

  const downloadTemplate = () => {
    const csv = CSV_TEMPLATE_HEADERS + '\n' + CSV_TEMPLATE_EXAMPLE;
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'doctors_import_template.csv';
    a.click();
  };

  const doImport = async () => {
    if (!preview?.length) return;
    setImporting(true);
    try {
      const { data } = await api.post('/doctors/import', { doctors: preview });
      setResult(data.data);
      if (data.data.inserted > 0) { toast.success(`Imported ${data.data.inserted} doctors`); onSaved(); }
    } catch(err) {
      setError(err.response?.data?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{maxWidth:640,width:'95%'}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <h3>Bulk Import Doctors</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:16}}>
          <div style={{background:'var(--bg-subtle)',borderRadius:'var(--radius-md)',padding:'12px 16px',fontSize:'0.85rem',color:'var(--text-secondary)'}}>
            Upload a CSV file with doctor data. <strong>Required columns:</strong> name, state. <strong>Optional:</strong> serial_no, district, town, speciality, qualification, contact, email, priority (A/B/C), journey_stage, fot_available (Yes/No), dob, doa.
            <div style={{marginTop:8}}>
              <button className="btn btn-ghost btn-sm" style={{color:'var(--brand-purple)',padding:'4px 0'}} onClick={downloadTemplate}>⬇ Download CSV Template</button>
            </div>
          </div>

          {!result && (
            <div
              style={{border:'2px dashed var(--border)',borderRadius:'var(--radius-md)',padding:'32px',textAlign:'center',cursor:'pointer',background:'var(--bg-subtle)'}}
              onClick={()=>fileRef.current?.click()}
            >
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{display:'none'}} onChange={handleFile}/>
              <div style={{fontSize:'2rem',marginBottom:8}}>📄</div>
              <div style={{fontWeight:600,color:'var(--text-primary)',marginBottom:4}}>{file ? file.name : 'Click to select CSV file'}</div>
              <div style={{fontSize:'0.8rem',color:'var(--text-muted)'}}>CSV or Excel (.xlsx) · UTF-8 encoding</div>
            </div>
          )}

          {error && <div style={{background:'#fef2f2',border:'1px solid #fca5a5',color:'#dc2626',padding:'10px 14px',borderRadius:'var(--radius-md)',fontSize:'0.85rem'}}>{error}</div>}

          {preview && !result && (
            <div>
              <div style={{fontWeight:600,marginBottom:8,color:'var(--text-primary)'}}>Preview — {preview.length} rows</div>
              <div style={{overflowX:'auto',maxHeight:240,border:'1px solid var(--border)',borderRadius:'var(--radius-md)'}}>
                <table style={{width:'100%',fontSize:'0.78rem',borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'var(--bg-subtle)',position:'sticky',top:0}}>
                      {['#','Name','State','District','Town','Speciality','Contact','Priority'].map(h=>(
                        <th key={h} style={{padding:'7px 10px',textAlign:'left',fontWeight:600,borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.slice(0,10).map((r,i)=>(
                      <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                        <td style={{padding:'6px 10px',color:'var(--text-muted)'}}>{i+1}</td>
                        <td style={{padding:'6px 10px',fontWeight:500}}>{r.name||'—'}</td>
                        <td style={{padding:'6px 10px'}}>{r.state||'—'}</td>
                        <td style={{padding:'6px 10px'}}>{r.district||'—'}</td>
                        <td style={{padding:'6px 10px'}}>{r.town||'—'}</td>
                        <td style={{padding:'6px 10px'}}>{r.speciality||'—'}</td>
                        <td style={{padding:'6px 10px'}}>{r.contact||'—'}</td>
                        <td style={{padding:'6px 10px'}}><span style={{fontWeight:600,color:r.priority==='A'?'#16a34a':r.priority==='B'?'#d97706':'#64748b'}}>{r.priority||'B'}</span></td>
                      </tr>
                    ))}
                    {preview.length>10&&<tr><td colSpan={8} style={{padding:'8px 10px',textAlign:'center',color:'var(--text-muted)',fontSize:'0.75rem'}}>…and {preview.length-10} more rows</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div style={{textAlign:'center',padding:'16px 0'}}>
              <div style={{fontSize:'2.5rem',marginBottom:8}}>✅</div>
              <div style={{fontWeight:700,fontSize:'1.1rem',color:'var(--text-primary)',marginBottom:8}}>Import Complete</div>
              <div style={{display:'flex',justifyContent:'center',gap:24,marginBottom:12}}>
                <div style={{textAlign:'center'}}><div style={{fontSize:'1.5rem',fontWeight:700,color:'#16a34a'}}>{result.inserted}</div><div style={{fontSize:'0.8rem',color:'var(--text-muted)'}}>Inserted</div></div>
                <div style={{textAlign:'center'}}><div style={{fontSize:'1.5rem',fontWeight:700,color:'#d97706'}}>{result.skipped}</div><div style={{fontSize:'0.8rem',color:'var(--text-muted)'}}>Skipped (duplicate)</div></div>
                <div style={{textAlign:'center'}}><div style={{fontSize:'1.5rem',fontWeight:700,color:'#dc2626'}}>{result.errors?.length||0}</div><div style={{fontSize:'0.8rem',color:'var(--text-muted)'}}>Errors</div></div>
              </div>
              {result.errors?.length>0&&(
                <div style={{background:'#fef2f2',borderRadius:'var(--radius-md)',padding:'10px 14px',textAlign:'left',fontSize:'0.8rem',color:'#dc2626',maxHeight:120,overflowY:'auto'}}>
                  {result.errors.map((e,i)=><div key={i}>Row {e.row} — {e.name}: {e.error}</div>)}
                </div>
              )}
            </div>
          )}

          <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
            {preview && !result && (
              <button className="btn btn-primary" onClick={doImport} disabled={importing}>
                {importing ? 'Importing…' : `Import ${preview.length} Doctors`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export { AddDoctorModal };
